import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import dialogCss from '@lib/style/dialog.module.scss';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { ModalName, useModalRegistration } from '@stores/ModalStore';
import { FormEvent, ReactNode, useState } from 'react';
import css from './FolderDialogs.module.scss';

/**
 * The frame every folder dialog shares: a dialog on desktop, a bottom sheet on a phone, with
 * Cancel and one confirm action. The body is a form, so Enter confirms from the name field.
 */
export function FolderDialogShell({
  modalName,
  open,
  onClose,
  title,
  subtitle,
  children,
  confirmLabel,
  confirmVariant = 'primary',
  confirmDisabled = false,
  loadingText,
  onConfirm,
  leadingAction,
}: {
  modalName: ModalName;
  open: boolean;
  onClose: () => void;
  title: string;
  /** A line under the title — where a new folder goes. */
  subtitle?: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  confirmDisabled?: boolean;
  loadingText: string;
  /** Awaited; the dialog stays open, with the button showing `loadingText`, until it settles. */
  onConfirm: () => Promise<void>;
  /** A secondary action at the far left of the button row — "New folder" in the picker. */
  leadingAction?: ReactNode;
}) {
  const isMobile = useIsMobile();
  // One busy flag for both ways of confirming — Enter in a field and the button — so neither
  // can send the write a second time while the first is in flight.
  const [busy, setBusy] = useState(false);
  useModalRegistration(modalName, open);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (confirmDisabled || busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const actions = (
    <>
      {leadingAction && <div className={css.leadingAction}>{leadingAction}</div>}
      <ActionButton variant="secondary" onClick={onClose} type="button">
        Cancel
      </ActionButton>
      <ActionButton variant={confirmVariant} type="submit" disabled={confirmDisabled || busy}>
        {busy ? loadingText : confirmLabel}
      </ActionButton>
    </>
  );

  if (isMobile) {
    return (
      <MobileDrawer open={open} onClose={onClose} onOpen={() => {}}>
        <form className={css.sheet} onSubmit={submit}>
          <span className={css.sheetTitle}>
            {title}
            {subtitle && <span className={css.subtitle}>{subtitle}</span>}
          </span>
          {children}
          <div className={css.sheetActions}>{actions}</div>
        </form>
      </MobileDrawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} className={dialogCss.dialog}>
      <form onSubmit={submit}>
        <DialogTitle className={dialogCss.dialogTitle}>
          {title}
          {subtitle && <span className={css.subtitle}>{subtitle}</span>}
        </DialogTitle>
        <DialogContent className={dialogCss.dialogContent}>{children}</DialogContent>
        <DialogActions className={dialogCss.dialogActions}>{actions}</DialogActions>
      </form>
    </Dialog>
  );
}
